import { useCallback, useMemo, useState, type ReactElement } from 'react';
import { ArrowRight } from 'lucide-react';
import { CopyTextPopover } from '@/pages/organization/components/CopyTextPopover';
import { CopyToClipboardButton } from '@/components/shared/CopyToClipboardButton';
import { useResizeObserver } from '@/hooks/useResizeObserver';
import { DEFAULT_MEASURE_FONT, measureTextWidth } from '@/lib/measureTextWidth';
import {
  normalizePathSeparators,
  truncatePathToWidth,
} from '@/lib/pathTruncation';
import { TextHighlight } from '@/components/shared/TextHighlight';
import { cn } from '@/lib/utils';

interface MiddleTruncatedPathProps {
  /** The path (or path-like label) to display and truncate. */
  path: string;
  /**
   * Full text shown/copied in the popover. Defaults to the normalized `path`.
   * Pass a joined list when a row represents multiple paths.
   */
  copyText?: string;
  /**
   * Active DataTable search term. When set, matches within the visible
   * (middle-truncated) text are highlighted. Matches elided by truncation
   * won't highlight; the full path stays discoverable via the hover title
   * and copy popover.
   */
  searchTerm?: string;
  /** Extra classes for the visible (truncated) text span. */
  className?: string;
  /** KIR-3810: opt-in one-click copy button beside the popover (alerts only). Default off keeps other surfaces popover-only. */
  showInlineCopy?: boolean;
}

/**
 * Renders a file path with responsive, width-measured middle truncation
 * (KIR-2563): the widest representation that fits the cell is shown, always
 * preserving the filename. The full raw path stays discoverable via the native
 * hover title and, when the path is truncated, copyable via a dedicated
 * click-to-open popover affordance.
 *
 * Only that copy affordance intercepts clicks; the path text itself stays inert
 * so clicking the cell still triggers the surrounding row action (e.g. expand)
 * exactly as before.
 *
 * KIR-3810: `showInlineCopy` adds a one-click copy button beside the popover.
 */
export function MiddleTruncatedPath({
  path,
  copyText,
  searchTerm,
  className,
  showInlineCopy = false,
}: MiddleTruncatedPathProps): ReactElement {
  const normalized = normalizePathSeparators(path);
  const fullText = copyText ?? normalized;

  // The ResizeObserver already fires on column/cell resizes, so skip the
  // redundant per-instance window `resize` listener (one per rendered cell).
  const { resizeRef, width } = useResizeObserver({
    subscribeWindowResize: false,
  });
  const [font, setFont] = useState(DEFAULT_MEASURE_FONT);

  // Attach the ResizeObserver and read the element's rendered font on mount.
  // Font is width-independent, so reading it once here (in the ref callback,
  // which runs at commit with styles applied) avoids a per-cell
  // `getComputedStyle` on every resize tick — a layout-thrash source when a
  // column drag resizes every row at once. Zoom scales font and width together
  // (both CSS px), so measurement stays proportional without re-reading.
  const setRef = useCallback(
    (node: HTMLElement | null): void => {
      resizeRef(node);
      if (node && typeof window !== 'undefined') {
        const styles = window.getComputedStyle(node);
        const nextFont =
          styles.font || `${styles.fontSize} ${styles.fontFamily}`.trim();
        setFont(prev => (prev === nextFont ? prev : nextFont));
      }
    },
    [resizeRef]
  );

  const displayed = useMemo(() => {
    if (width <= 0) {
      // `normalized` is derived from `path` (already in deps); recompute here so
      // the memo doesn't take a redundant dependency on it.
      return normalizePathSeparators(path);
    }
    // `truncatePathToWidth` normalizes internally, so pass the raw path.
    return truncatePathToWidth(path, width, text =>
      measureTextWidth(text, font)
    );
  }, [path, width, font]);

  // The path is elided, or the popover carries more than the visible path (a
  // multi-path row): either way the copy affordance must stay discoverable.
  const isTruncated =
    displayed !== normalized || (copyText != null && copyText !== normalized);

  // The outer element is width-driven (`w-full`) so the measured text span
  // tracks the cell width — widening the column restores a fuller path.
  return (
    <span className="group/mtp flex w-full min-w-0 items-center gap-1">
      <span
        ref={setRef}
        // Always carry the full path as the native hover title (not gated on
        // truncation). It is the single, truncation-independent tooltip for the
        // cell, keeps the full path discoverable on hover even when it fits, and
        // is the stable DOM carrier the e2e File-column matcher keys on
        // (getByTitle) regardless of column width. The copy button no longer
        // sets its own title, so there's no double tooltip.
        title={fullText}
        className={cn(
          'block min-w-0 flex-1 overflow-hidden whitespace-nowrap font-mono',
          className
        )}
      >
        <TextHighlight text={displayed} searchTerm={searchTerm ?? ''} />
      </span>
      {/* KIR-3810: one-click copy of the full path. Hidden + pointer-events-none
          until hover/focus so its reserved box can't steal a tap (touch/no-hover);
          stops propagation so the row action doesn't fire. */}
      {showInlineCopy && (
        <span
          onPointerDown={event => event.stopPropagation()}
          onClick={event => event.stopPropagation()}
          className="pointer-events-none inline-flex shrink-0 opacity-0 transition-opacity group-hover/mtp:pointer-events-auto group-hover/mtp:opacity-100 focus-within:pointer-events-auto focus-within:opacity-100"
        >
          <CopyToClipboardButton
            text={fullText}
            compactCopiedState
            variant="ghost"
            aria-label="Copy file path"
            className="h-6 w-6 p-1 text-muted-foreground"
          >
            <span className="sr-only">Copy file path</span>
          </CopyToClipboardButton>
        </span>
      )}
      {/* Dedicated copy affordance. It intercepts pointer/click so opening the
          popover never triggers the row action, and always reserves its box so
          toggling `isTruncated` can't change the measured width (no flip-flop).
          It is only interactive once the path is actually truncated. */}
      <span
        onPointerDown={event => event.stopPropagation()}
        onClick={event => event.stopPropagation()}
        className={cn(
          'inline-flex shrink-0',
          !isTruncated && 'pointer-events-none'
        )}
      >
        <CopyTextPopover
          fullText={fullText}
          bodyClassName="font-mono"
          triggerAriaLabel={fullText}
          triggerInert={!isTruncated}
          triggerClassName="hover:opacity-80 transition-opacity"
        >
          <ArrowRight
            aria-hidden
            className={cn(
              'h-3 w-3 text-muted-foreground',
              !isTruncated && 'invisible'
            )}
          />
        </CopyTextPopover>
      </span>
    </span>
  );
}

MiddleTruncatedPath.displayName = 'MiddleTruncatedPath';
